import { Button, CloseButton } from '@ui/shared/Button';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { closePopup } from '../../../data/providers/popupsProvider';
import { IAthlete, IGroup, ITeam } from '../../../data/types';
import { saveAthletes } from '../../../data/writers/athletes';
import { archiveGroup } from '../../../data/writers/groups';
import { useEntities } from '../../../hooks/useEntities';
import { CardBody } from '../../shared/card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../shared/card/CardHeader';
import { LinearLoading } from '../../shared/loading/LinearLoading';
import { PopupCard, PopupCardContent } from '../../shared/popups/PopupCard';
import { ArchiveForm } from './ArchiveForm';

interface IPopupProps {
  onClose: () => void;
  team?: ITeam;
}

const Popup = (props: IPopupProps): React.ReactElement => {
  const isEdit = Boolean(props?.team);

  const [team] = React.useState<Partial<ITeam>>(
    isEdit
      ? props.team
      : {
          name: '',
          image: null,
        }
  );
  const [selectedGroups, setSelectedGroups] = React.useState<IGroup[]>([]);
  const [selectedAthletes, setSelectedAthletes] = React.useState<IAthlete[]>(
    []
  );
  const [saving, setSaving] = React.useState<boolean>(false);

  /// subscribe to entities
  const { athletes, teams, groups, loading } = useEntities('archived');

  /**
   * @description set the selected groups and athletes based on the supplied team
   */
  React.useEffect((): void => {
    if (props.team && !loading) {
      const mSelectedGroups = groups.filter((g: IGroup): boolean =>
        g.teams?.includes(props.team.id)
      );
      const mSelectedAthletes = athletes.filter((a: IAthlete): boolean =>
        a.teams?.includes(props.team.id)
      );
      setSelectedGroups(mSelectedGroups);
      setSelectedAthletes(mSelectedAthletes);
    }
  }, [loading]);

  /**
   * @description handles the selection/de-selection of athletes
   * @param id
   * @param checked
   */
  const athleteSelectionHandler = (athlete: ITeam, checked: boolean): void => {
    let mSelectedAthletes = [...selectedAthletes];
    if (checked) {
      mSelectedAthletes.push(athlete);
    } else {
      mSelectedAthletes = mSelectedAthletes.filter(
        (a: IAthlete): boolean => a.id !== athlete.id
      );
    }
    setSelectedAthletes(mSelectedAthletes);
  };

  /**
   * @description handles the selection/de-selection of a group
   * @param id
   * @param checked
   */
  const groupSelectionHandler = (group: IGroup, checked: boolean): void => {
    let mSelectedGroups = [...selectedGroups];
    if (checked) {
      mSelectedGroups.push(group);
    } else {
      mSelectedGroups = mSelectedGroups.filter(
        (g: IGroup): boolean => g.id !== group.id
      );
    }
    setSelectedGroups(mSelectedGroups);
  };

  /**
   * @description selects all the available athletes
   */
  const selectAllGroups = (): void => setSelectedGroups(groups);
  /**
   * @description clears the currently selected athletes
   */
  const clearAllGroups = (): void => setSelectedGroups([]);

  /**
   * @description selects all the available athletes
   */
  const selectAllAthletes = (): void => setSelectedAthletes(athletes);

  /**
   * @description clears the currently selected athletes
   */
  const clearAllAthletes = (): void => setSelectedAthletes([]);

  /**
   * @description restores active status of selected groups and athletes
   */
  const restoreEntities = async (): Promise<void> => {
    setSaving(true);

    if (selectedAthletes.length > 0) {
      const updatedAthletes = selectedAthletes.map(
        (athlete: IAthlete): IAthlete => ({
          ...athlete,
          active: true,
        })
      );
      await saveAthletes(updatedAthletes);
    }
    if (selectedGroups.length > 0) {
      selectedGroups.forEach((group: IGroup): void => {
        archiveGroup({
          ...group,
          active: true,
        });
      });
    }
    setSaving(false);
    props.onClose();
  };

  return (
    <PopupCard onClose={props.onClose} size='large' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h5>Manage Archive</h5>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>

        <CardBody>
          {saving && <LinearLoading />}
          {loading && <LinearLoading />}

          {!loading && (
            <ArchiveForm
              athletes={athletes}
              athleteSelectionHandler={athleteSelectionHandler}
              clearAllAthletes={clearAllAthletes}
              clearAllGroups={clearAllGroups}
              groups={groups}
              groupSelectionHandler={groupSelectionHandler}
              selectAllAthletes={selectAllAthletes}
              selectAllGroups={selectAllGroups}
              selectedAthletes={selectedAthletes}
              selectedGroups={selectedGroups}
              team={team}
              teams={teams}
            />
          )}
        </CardBody>
        <CardFooter surface={8}>
          <CardFooterLeft>
            <p style={{ color: 'var(--color-text)' }}>
              Deactivated groups and athletes appear here. To re-activate a
              group or athlete, toggle the radio buttons in each row or use
              Select All button(s), and click Restore.
            </p>
          </CardFooterLeft>
          <CardFooterRight>
            <Button
              title='Restore'
              type='submit'
              border={'none'}
              color={'light'}
              flavor={'primary'}
              onClick={restoreEntities}
              disabled={saving}
              icon='backup'
            />
          </CardFooterRight>
        </CardFooter>
      </PopupCardContent>
    </PopupCard>
  );
};

interface IProps extends RouteComponentProps {
  team?: ITeam;
  onClose: () => void;
}

const ManageArchive = withRouter((props: IProps): React.ReactElement => {
  return <Popup onClose={closePopup(props.history)} team={props.team} />;
});

export { ManageArchive };
